import React, { useState } from 'react';
import {
  Users,
  ShieldAlert,
  Clock,
  HeartHandshake,
  RotateCw,
  Sparkles,
  Copy,
  Check,
  Send,
  Printer,
  Utensils,
  GlassWater,
  AlertOctagon,
  ChevronRight,
  Activity,
  Bed,
  PhoneCall,
  UserCheck,
  MessageSquareHeart
} from 'lucide-react';
import { Resident, FamilyDigestSubject } from '../types';
import { generateFamilyUpdate } from '../services/geminiService';

interface SeniorLivingSuiteProps {
  residents: Resident[];
  onResetTurn: (residentId: string) => void;
  onOpenPocketCard: (resident: Resident) => void;
  onOpenFamilyDigest?: (subject: FamilyDigestSubject) => void;
}

export const SeniorLivingSuite: React.FC<SeniorLivingSuiteProps> = ({
  residents,
  onResetTurn,
  onOpenPocketCard,
  onOpenFamilyDigest
}) => {
  const [selectedResident, setSelectedResident] = useState<Resident>(residents[0]);
  const [filterMode, setFilterMode] = useState<'all' | 'turnsDue' | 'highFallRisk'>('all');
  const [generatedFamilyNote, setGeneratedFamilyNote] = useState<string>('');
  const [isGeneratingNote, setIsGeneratingNote] = useState<boolean>(false);
  const [copiedNote, setCopiedNote] = useState<boolean>(false);

  // Filter residents
  const filteredResidents = residents.filter((r) => {
    if (filterMode === 'turnsDue') return r.turnMinutesRemaining <= 30;
    if (filterMode === 'highFallRisk') return r.fallRisk.includes('High');
    return true;
  });

  const turnsDueSoonCount = residents.filter((r) => r.turnMinutesRemaining <= 30).length;
  const highFallRiskCount = residents.filter((r) => r.fallRisk.includes('High')).length;

  const handleGenerateFamilyNote = async (resident: Resident) => {
    setSelectedResident(resident);
    setIsGeneratingNote(true);
    setCopiedNote(false);
    const note = await generateFamilyUpdate(resident);
    setGeneratedFamilyNote(note);
    setIsGeneratingNote(false);
  };

  const handleLaunchFamilyDigestModal = (resident: Resident) => {
    if (!onOpenFamilyDigest) return;
    const subject: FamilyDigestSubject = {
      id: resident.id,
      name: resident.fullName,
      preferredName: resident.preferredName,
      recipientName: resident.primaryEmergencyContact.name,
      recipientRelation: resident.primaryEmergencyContact.relation,
      recipientPhone: resident.primaryEmergencyContact.phone,
      sleepScoreOrState: `Resting peacefully (${resident.dailyLog.restPeriods})`,
      vitalsSummary: `BP ${resident.vitals.bpSystolic}/${resident.vitals.bpDiastolic} mmHg, HR ${resident.vitals.heartRate} bpm, SpO2 ${resident.vitals.spO2}%`,
      nutritionActivitySummary: `Completed ${resident.dailyLog.lunchIntakePct}% of lunch, ${resident.dailyLog.hydrationOz} oz fluids, Activity: ${resident.dailyLog.activity}`,
      locationOrRoom: resident.roomNumber
    };
    onOpenFamilyDigest(subject);
  };

  const handleCopyNote = () => {
    if (!generatedFamilyNote) return;
    navigator.clipboard.writeText(generatedFamilyNote);
    setCopiedNote(true);
    setTimeout(() => setCopiedNote(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Top Suite Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-2xl relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">Total Residents</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/10 text-teal-400 border border-teal-500/20">
              MEMORY & SKILLED
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-white">{residents.length}</span>
            <span className="text-xs text-slate-400">in Active Cottage</span>
          </div>
          <p className="text-[11px] text-teal-400 mt-2 flex items-center gap-1 font-mono">
            <UserCheck className="h-3.5 w-3.5" /> 100% Caregiver Shift Coverage
          </p>
        </div>

        <div className="bg-slate-900/80 border border-rose-500/30 p-4 rounded-2xl relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">High Fall Risk</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
              BED ALARMS ACTIVE
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-rose-400">{highFallRiskCount}</span>
            <span className="text-xs text-slate-400">Residents flagged</span>
          </div>
          <p className="text-[11px] text-rose-300 mt-2 flex items-center gap-1">
            <ShieldAlert className="h-3.5 w-3.5" /> Morse score &gt; 65
          </p>
        </div>

        <div className="bg-slate-900/80 border border-amber-500/30 p-4 rounded-2xl relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">2-Hour Turns Due</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
              Q2H PROTOCOL
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-amber-400">{turnsDueSoonCount}</span>
            <span className="text-xs text-slate-400">&lt; 30m or Overdue</span>
          </div>
          <p className="text-[11px] text-amber-300 mt-2 flex items-center gap-1">
            <RotateCw className="h-3.5 w-3.5" /> Prevents skin breakdown & ulcers
          </p>
        </div>

        <div className="bg-slate-900/80 border border-cyan-500/30 p-4 rounded-2xl relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">Family Updates</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
              GEMINI 2.5 FLASH
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-cyan-300">Empathetic</span>
            <span className="text-xs text-slate-400">3-Sentence AI</span>
          </div>
          <p className="text-[11px] text-cyan-400 mt-2 flex items-center gap-1">
            <HeartHandshake className="h-3.5 w-3.5" /> Instant SMS & Portal sharing
          </p>
        </div>
      </div>

      {/* Main Suite Split Layout: Resident Care & Fall Risk Board (Left) & Family Update Engine (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Resident Care & Fall Risk Board */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-800">
              <div>
                <h2 className="text-base font-bold text-white flex items-center gap-2">
                  <Bed className="h-5 w-5 text-teal-400" />
                  <span>Resident Care & Fall Risk Board</span>
                </h2>
                <p className="text-xs text-slate-400">
                  Track ADL transfer types, Hoyer lift protocols, and 2-hour turn schedules.
                </p>
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs">
                <button
                  onClick={() => setFilterMode('all')}
                  className={`px-2.5 py-1 rounded-lg font-medium transition ${
                    filterMode === 'all' ? 'bg-teal-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  All ({residents.length})
                </button>
                <button
                  onClick={() => setFilterMode('turnsDue')}
                  className={`px-2.5 py-1 rounded-lg font-medium transition flex items-center gap-1 ${
                    filterMode === 'turnsDue' ? 'bg-amber-400 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <span>Turns Due</span>
                  {turnsDueSoonCount > 0 && (
                    <span className="h-4 w-4 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">
                      {turnsDueSoonCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setFilterMode('highFallRisk')}
                  className={`px-2.5 py-1 rounded-lg font-medium transition ${
                    filterMode === 'highFallRisk' ? 'bg-rose-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  High Risk ({highFallRiskCount})
                </button>
              </div>
            </div>

            {/* Resident Cards Grid */}
            <div className="space-y-3.5 mt-4">
              {filteredResidents.map((resident) => {
                const isOverdue = resident.turnMinutesRemaining === 0;
                const isDueSoon = resident.turnMinutesRemaining > 0 && resident.turnMinutesRemaining <= 30;
                const isSelected = selectedResident.id === resident.id;

                return (
                  <div
                    key={resident.id}
                    onClick={() => setSelectedResident(resident)}
                    className={`p-4 rounded-2xl border transition relative cursor-pointer ${
                      isSelected
                        ? 'bg-slate-900 border-teal-500 shadow-lg shadow-teal-500/10'
                        : 'bg-slate-950/60 border-slate-800/80 hover:border-slate-700'
                    }`}
                  >
                    {/* Top Row: Name, Room, Code Status, Fall Flag */}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-slate-100 text-sm">
                            {resident.fullName}{' '}
                            <span className="text-teal-400 font-normal">("{resident.preferredName}")</span>
                          </h3>
                          <span className="text-xs font-mono text-slate-400">{resident.age}y</span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                            {resident.roomNumber}
                          </span>
                        </div>

                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {resident.primaryDiagnosis}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span
                          className={`text-[9px] font-mono px-2 py-0.5 rounded font-bold ${
                            resident.codeStatus === 'DNR'
                              ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                              : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                          }`}
                        >
                          {resident.codeStatus}
                        </span>

                        <span
                          className={`text-[9px] font-mono px-2 py-0.5 rounded font-bold flex items-center gap-1 ${
                            resident.fallRisk.includes('High')
                              ? 'bg-rose-500 text-slate-950 animate-pulse'
                              : 'bg-amber-400/20 text-amber-300 border border-amber-400/40'
                          }`}
                        >
                          <AlertOctagon className="h-2.5 w-2.5" />
                          <span>{resident.fallRisk}</span>
                        </span>
                      </div>
                    </div>

                    {/* Care Badges: ADL Transfer & Dietary Texture */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                      <div className="p-2 rounded-xl bg-slate-900 border border-slate-800/90 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <span className="h-2 w-2 rounded-full bg-cyan-400"></span>
                          <span>Transfer ADL:</span>
                        </div>
                        <span className="font-bold text-slate-200 font-mono">
                          {resident.mobilityTransfer}
                        </span>
                      </div>

                      <div className="p-2 rounded-xl bg-slate-900 border border-slate-800/90 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <Utensils className="h-3 w-3 text-amber-400" />
                          <span>Diet / Fluid:</span>
                        </div>
                        <span className="font-semibold text-slate-200">
                          {resident.dietaryTexture} • {resident.fluidConsistency}
                        </span>
                      </div>
                    </div>

                    {/* Turn Timer & Quick Action Bar */}
                    <div className="mt-3 pt-3 border-t border-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                      {/* Repositioning Timer */}
                      <div className="flex items-center gap-2">
                        <Clock className={`h-4 w-4 ${isOverdue ? 'text-rose-400 animate-spin' : isDueSoon ? 'text-amber-400' : 'text-teal-400'}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-slate-400">2h Turn Timer:</span>
                            <span
                              className={`font-mono font-bold text-xs ${
                                isOverdue
                                  ? 'text-rose-400 font-extrabold'
                                  : isDueSoon
                                  ? 'text-amber-400'
                                  : 'text-teal-300'
                              }`}
                            >
                              {isOverdue ? 'OVERDUE (Turn Now)' : `${resident.turnMinutesRemaining} mins remaining`}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-500">
                            Current position: <span className="text-slate-300 font-medium">{resident.turnSide}</span> (last @ {resident.lastTurnTimestamp})
                          </div>
                        </div>
                      </div>

                      {/* Caregiver Actions */}
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLaunchFamilyDigestModal(resident);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-pink-500/15 hover:bg-pink-500/25 text-pink-300 border border-pink-500/30 transition flex items-center gap-1.5 font-semibold text-xs shadow-sm"
                        >
                          <MessageSquareHeart className="h-3.5 w-3.5 text-pink-400" />
                          <span>Generate Family Digest</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onResetTurn(resident.id);
                          }}
                          className="px-2.5 py-1.5 rounded-lg bg-teal-500/15 hover:bg-teal-500/25 text-teal-300 border border-teal-500/30 transition flex items-center gap-1 font-semibold text-xs"
                          title="Log 2-hour turn completed now"
                        >
                          <RotateCw className="h-3 w-3" />
                          <span>Turn Done</span>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenPocketCard(resident);
                          }}
                          className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition flex items-center gap-1 font-semibold text-xs"
                          title="Print Pocket Caregiver Cheat-Sheet"
                        >
                          <Printer className="h-3 w-3 text-indigo-400" />
                          <span>Pocket Card</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Family Update Generator & Quick Pocket Card Preview */}
        <div className="lg:col-span-5 space-y-4">
          {/* AI Family Update Generator Box */}
          <div className="bg-slate-900/80 border border-cyan-500/40 rounded-2xl p-5 shadow-xl relative">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                  <HeartHandshake className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                    <span>Family Update Generator</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-semibold">
                      GEMINI 2.5 FLASH
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    Warm, empathetic 3-sentence daily summary for loved ones.
                  </p>
                </div>
              </div>
            </div>

            {/* Target Resident Details */}
            <div className="mt-4 p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-200">
                  {selectedResident.preferredName} ({selectedResident.fullName})
                </span>
                <span className="font-mono text-[10px] text-teal-400">{selectedResident.roomNumber}</span>
              </div>
              <div className="text-[11px] text-slate-400 space-y-1">
                <div>
                  <strong className="text-slate-300">Activity:</strong> {selectedResident.dailyLog.activity}
                </div>
                <div>
                  <strong className="text-slate-300">Mood:</strong> {selectedResident.dailyLog.mood}
                </div>
                <div className="flex items-center gap-4 text-teal-400 font-mono text-[10px] pt-1">
                  <span>Lunch Intake: {selectedResident.dailyLog.lunchIntakePct}%</span>
                  <span>Hydration: {selectedResident.dailyLog.hydrationOz} oz</span>
                  <span>SpO2: {selectedResident.vitals.spO2}%</span>
                </div>
              </div>
            </div>

            {/* Generator Action */}
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={() => handleLaunchFamilyDigestModal(selectedResident)}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-slate-950 font-bold py-2.5 px-4 rounded-xl transition flex items-center justify-center gap-2 text-xs shadow-lg shadow-pink-500/20"
              >
                <MessageSquareHeart className="h-4 w-4" />
                <span>Open Family Digest SMS Composer</span>
              </button>

              <button
                onClick={() => handleGenerateFamilyNote(selectedResident)}
                disabled={isGeneratingNote}
                className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold py-2 px-4 rounded-xl transition flex items-center justify-center gap-2 text-xs border border-slate-700 disabled:opacity-50"
              >
                <Sparkles className={`h-4 w-4 ${isGeneratingNote ? 'animate-spin' : ''}`} />
                <span>
                  {isGeneratingNote
                    ? `Generating Note for ${selectedResident.preferredName}...`
                    : `Quick Inline Summary for ${selectedResident.preferredName}`}
                </span>
              </button>
            </div>

            {/* Generated Note Container */}
            {generatedFamilyNote && (
              <div className="mt-4 p-4 rounded-xl bg-slate-950 border border-cyan-500/30 space-y-3">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-cyan-300 flex items-center gap-1">
                    <Send className="h-3 w-3" /> Ready for Family SMS / Portal:
                  </span>
                  <span className="text-[10px] font-mono text-slate-500">
                    To: {selectedResident.primaryEmergencyContact.name} ({selectedResident.primaryEmergencyContact.phone})
                  </span>
                </div>

                <div className="p-3 bg-slate-900/90 rounded-lg border border-slate-800 text-xs text-slate-200 leading-relaxed font-sans italic">
                  "{generatedFamilyNote}"
                </div>

                <div className="flex items-center justify-between pt-1">
                  <span className="text-[10px] text-slate-500 font-mono">
                    3-sentence empathetic standard • Verified
                  </span>
                  <button
                    onClick={handleCopyNote}
                    className="flex items-center gap-1 text-xs bg-slate-800 hover:bg-slate-700 text-teal-300 border border-slate-700 px-3 py-1.5 rounded-lg transition"
                  >
                    {copiedNote ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    <span>{copiedNote ? 'Copied to Clipboard!' : 'Copy Message'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Pocket Caregiver Card Quick Preview */}
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Printer className="h-4 w-4 text-indigo-400" />
                <h4 className="text-xs font-bold text-white">Pocket Caregiver Cheat-Sheet</h4>
              </div>
              <button
                onClick={() => onOpenPocketCard(selectedResident)}
                className="text-[11px] text-indigo-300 hover:text-indigo-200 font-semibold underline underline-offset-2"
              >
                Open Full Printout
              </button>
            </div>

            <p className="text-[11px] text-slate-400">
              Compact lanyard-sized badge card with dietary consistency, transfer protocol, and emergency proxy info for shift changes.
            </p>

            <div className="border border-dashed border-slate-700 p-3.5 rounded-xl bg-slate-950 font-mono text-[11px] space-y-2">
              <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                <span className="font-bold text-slate-200 text-xs">{selectedResident.fullName}</span>
                <span className="bg-slate-800 px-1.5 py-0.5 rounded text-[10px] text-teal-300">
                  {selectedResident.roomNumber}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div>
                  <span className="text-slate-500 block">TRANSFER:</span>
                  <span className="font-bold text-cyan-300">{selectedResident.mobilityTransfer}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">DIET / LIQUID:</span>
                  <span className="font-bold text-amber-300">
                    {selectedResident.dietaryTexture} / {selectedResident.fluidConsistency}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">FALL RISK:</span>
                  <span className="font-bold text-rose-400">{selectedResident.fallRisk}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">CODE:</span>
                  <span className="font-bold text-emerald-400">{selectedResident.codeStatus}</span>
                </div>
              </div>
              <div className="pt-1 border-t border-slate-800 text-[9px] text-slate-400 flex items-center justify-between">
                <span>Emergency: {selectedResident.primaryEmergencyContact.name}</span>
                <span className="text-slate-300">{selectedResident.primaryEmergencyContact.phone}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
